<template>
  <div class="exam-analytics-dashboard" v-loading="loading">
    <!-- No Data State -->
    <el-empty v-if="!loading && analytics.total_attempts === 0" description="No exam attempts yet">
      <p class="empty-hint">Students will appear here after they take the exam</p>
    </el-empty>

    <template v-else-if="!loading">
      <!-- Overview Cards -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-value">{{ analytics.total_attempts }}</div>
          <div class="stat-label">Total Attempts</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ analytics.unique_students }}</div>
          <div class="stat-label">Unique Students</div>
        </div>
        <div class="stat-card highlight-success">
          <div class="stat-value">{{ analytics.pass_rate }}%</div>
          <div class="stat-label">Pass Rate</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ analytics.average_score }}%</div>
          <div class="stat-label">Average Score</div>
        </div>
      </div>

      <!-- Charts Row -->
      <div class="charts-row">
        <!-- Score Distribution -->
        <el-card class="chart-card">
          <template #header>
            <span>Score Distribution</span>
          </template>
          <div class="score-distribution">
            <div
              v-for="(count, range) in analytics.score_distribution"
              :key="range"
              class="distribution-bar"
            >
              <div class="bar-label">{{ range }}%</div>
              <div class="bar-container">
                <div
                  class="bar-fill"
                  :style="{ width: getBarWidth(count) + '%' }"
                  :class="getBarClass(range)"
                ></div>
              </div>
              <div class="bar-count">{{ count }}</div>
            </div>
          </div>
        </el-card>

        <!-- Trends Chart -->
        <el-card class="chart-card">
          <template #header>
            <span>Daily Trends (Last 30 Days)</span>
          </template>
          <div class="trends-chart" v-if="analytics.trends && analytics.trends.length > 0">
            <div class="trend-bars">
              <div
                v-for="day in analytics.trends"
                :key="day.date"
                class="trend-bar-group"
                :title="`${day.date}: ${day.attempts} attempts, ${day.passed} passed`"
              >
                <div
                  class="trend-bar"
                  :style="{ height: getTrendHeight(day.attempts) + 'px' }"
                ></div>
              </div>
            </div>
            <div class="trend-legend">
              <span>{{ analytics.trends[0]?.date }}</span>
              <span>{{ analytics.trends[analytics.trends.length - 1]?.date }}</span>
            </div>
          </div>
          <el-empty v-else description="No trend data" :image-size="60" />
        </el-card>
      </div>

      <!-- Question Analysis -->
      <el-card class="section-card">
        <template #header>
          <div class="section-header">
            <span>Question Analysis</span>
            <span class="header-hint">Sorted by difficulty (hardest first)</span>
          </div>
        </template>
        <el-table :data="analytics.question_analysis" style="width: 100%" size="small">
          <el-table-column label="Question" min-width="300">
            <template #default="{ row }">
              <span class="question-preview">{{ row.question_preview }}...</span>
            </template>
          </el-table-column>
          <el-table-column label="Times Answered" width="120" align="center">
            <template #default="{ row }">
              {{ row.times_answered }}
            </template>
          </el-table-column>
          <el-table-column label="Correct Rate" width="140" align="center">
            <template #default="{ row }">
              <el-progress
                :percentage="row.correct_rate"
                :color="getCorrectRateColor(row.correct_rate)"
                :stroke-width="8"
                :show-text="true"
              />
            </template>
          </el-table-column>
          <el-table-column label="Difficulty" width="100" align="center">
            <template #default="{ row }">
              <el-tag
                :type="getDifficultyType(row.computed_difficulty)"
                size="small"
              >
                {{ row.computed_difficulty }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
      </el-card>

      <!-- Recent Attempts -->
      <el-card class="section-card">
        <template #header>
          <div class="section-header">
            <span>Recent Attempts</span>
            <el-button type="primary" text size="small" @click="showAllAttempts = true">
              View All
            </el-button>
          </div>
        </template>
        <el-table :data="analytics.recent_attempts" style="width: 100%" size="small">
          <el-table-column label="Student" min-width="150">
            <template #default="{ row }">
              <span class="student-name">{{ row.user_name }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Score" width="100" align="center">
            <template #default="{ row }">
              <span :class="row.passed ? 'score-pass' : 'score-fail'">
                {{ row.score_percentage }}%
              </span>
            </template>
          </el-table-column>
          <el-table-column label="Status" width="100" align="center">
            <template #default="{ row }">
              <el-tag :type="row.passed ? 'success' : 'danger'" size="small">
                {{ row.passed ? 'Passed' : 'Failed' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="Time Spent" width="100" align="center">
            <template #default="{ row }">
              {{ formatDuration(row.time_spent_seconds) }}
            </template>
          </el-table-column>
          <el-table-column label="App Switches" width="100" align="center">
            <template #default="{ row }">
              <span :class="row.app_switch_count > 0 ? 'warning-text' : ''">
                {{ row.app_switch_count }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="Date" width="160">
            <template #default="{ row }">
              {{ formatDate(row.submit_time) }}
            </template>
          </el-table-column>
          <el-table-column label="Actions" width="80" align="center">
            <template #default="{ row }">
              <el-button type="primary" link size="small" @click="viewAttemptDetail(row)">
                Detail
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </template>

    <!-- All Attempts Dialog -->
    <el-dialog
      v-model="showAllAttempts"
      title="All Exam Attempts"
      width="900px"
      :close-on-click-modal="false"
    >
      <div class="attempts-filters">
        <el-select v-model="attemptsFilter" placeholder="Filter by status" clearable @change="loadAttempts">
          <el-option label="All" value="" />
          <el-option label="Graded" value="graded" />
          <el-option label="In Progress" value="in_progress" />
        </el-select>
      </div>
      <el-table :data="allAttempts" style="width: 100%" v-loading="loadingAttempts">
        <el-table-column label="Student" min-width="150">
          <template #default="{ row }">
            {{ row.user_name }}
          </template>
        </el-table-column>
        <el-table-column label="Score" width="80" align="center">
          <template #default="{ row }">
            {{ row.score_percentage }}%
          </template>
        </el-table-column>
        <el-table-column label="Status" width="100" align="center">
          <template #default="{ row }">
            <el-tag
              :type="row.passed ? 'success' : (row.status === 'graded' ? 'danger' : 'info')"
              size="small"
            >
              {{ row.passed ? 'Passed' : (row.status === 'graded' ? 'Failed' : row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Questions" width="100" align="center">
          <template #default="{ row }">
            {{ row.correct_count }}/{{ row.total_questions }}
          </template>
        </el-table-column>
        <el-table-column label="Time" width="80" align="center">
          <template #default="{ row }">
            {{ formatDuration(row.time_spent_seconds) }}
          </template>
        </el-table-column>
        <el-table-column label="Date" width="140">
          <template #default="{ row }">
            {{ formatDate(row.submit_time || row.start_time) }}
          </template>
        </el-table-column>
        <el-table-column label="Actions" width="80">
          <template #default="{ row }">
            <el-button type="primary" link size="small" @click="viewAttemptDetail(row)">
              Detail
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-container" v-if="attemptsPagination.total > attemptsPagination.limit">
        <el-pagination
          v-model:current-page="attemptsPagination.page"
          :page-size="attemptsPagination.limit"
          :total="attemptsPagination.total"
          layout="prev, pager, next"
          @current-change="loadAttempts"
        />
      </div>
    </el-dialog>

    <!-- Attempt Detail Dialog -->
    <el-dialog
      v-model="showAttemptDetail"
      :title="`Exam Attempt - ${attemptDetail?.student?.user_name || ''}`"
      width="800px"
      :close-on-click-modal="false"
    >
      <div v-if="attemptDetail" class="attempt-detail">
        <!-- Summary -->
        <div class="detail-summary">
          <div class="summary-item">
            <span class="summary-label">Score</span>
            <span class="summary-value" :class="attemptDetail.passed ? 'text-success' : 'text-danger'">
              {{ attemptDetail.score_percentage }}%
            </span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Result</span>
            <el-tag :type="attemptDetail.passed ? 'success' : 'danger'">
              {{ attemptDetail.passed ? 'PASSED' : 'FAILED' }}
            </el-tag>
          </div>
          <div class="summary-item">
            <span class="summary-label">Correct</span>
            <span class="summary-value">{{ attemptDetail.correct_count }}/{{ attemptDetail.total_questions }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Time</span>
            <span class="summary-value">{{ formatDuration(attemptDetail.time_spent_seconds) }}</span>
          </div>
          <div class="summary-item" v-if="attemptDetail.proctoring_enabled">
            <span class="summary-label">App Switches</span>
            <span class="summary-value" :class="attemptDetail.app_switch_count > 0 ? 'text-warning' : ''">
              {{ attemptDetail.app_switch_count }}
            </span>
          </div>
        </div>

        <!-- Questions Review -->
        <div class="questions-review">
          <h4>Questions Review</h4>
          <div
            v-for="(q, index) in attemptDetail.questions"
            :key="index"
            class="question-item"
            :class="q.is_correct ? 'correct' : 'incorrect'"
          >
            <div class="question-header">
              <span class="question-number">Q{{ index + 1 }}</span>
              <el-tag :type="q.is_correct ? 'success' : 'danger'" size="small">
                {{ q.is_correct ? 'Correct' : 'Incorrect' }}
              </el-tag>
            </div>
            <div class="question-text">{{ q.question }}</div>
            <div class="question-options">
              <div
                v-for="(opt, optIndex) in q.options"
                :key="optIndex"
                class="option-item"
                :class="{
                  'correct-answer': ['A','B','C','D'][optIndex] === q.correct_answer,
                  'student-answer': ['A','B','C','D'][optIndex] === q.student_answer,
                  'wrong-answer': ['A','B','C','D'][optIndex] === q.student_answer && !q.is_correct
                }"
              >
                <span class="option-label">{{ ['A','B','C','D'][optIndex] }}</span>
                <span class="option-text">{{ opt }}</span>
              </div>
            </div>
            <div class="question-explanation" v-if="q.explanation">
              <strong>Explanation:</strong> {{ q.explanation }}
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'

export default {
  name: 'ExamAnalyticsDashboard',
  props: {
    collectionId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      analytics: {
        total_attempts: 0,
        unique_students: 0,
        pass_rate: 0,
        average_score: 0,
        score_distribution: {},
        question_analysis: [],
        recent_attempts: [],
        trends: []
      },
      showAllAttempts: false,
      loadingAttempts: false,
      allAttempts: [],
      attemptsFilter: '',
      attemptsPagination: {
        page: 1,
        limit: 20,
        total: 0
      },
      showAttemptDetail: false,
      attemptDetail: null
    }
  },
  watch: {
    collectionId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.fetchAnalytics()
        }
      }
    }
  },
  methods: {
    async fetchAnalytics() {
      this.loading = true
      try {
        const response = await postRequest('getExamAnalytics', {
          collection_id: this.collectionId,
          user_id: this.$store.state.user_id
        })

        if (response.data.code === 0 && response.data.data) {
          this.analytics = response.data.data
        }
      } catch (error) {
        console.error('Error fetching analytics:', error)
      } finally {
        this.loading = false
      }
    },

    async loadAttempts() {
      this.loadingAttempts = true
      try {
        const response = await postRequest('getExamAttempts', {
          collection_id: this.collectionId,
          user_id: this.$store.state.user_id,
          page: this.attemptsPagination.page,
          limit: this.attemptsPagination.limit,
          status: this.attemptsFilter || undefined
        })

        if (response.data.code === 0 && response.data.data) {
          this.allAttempts = response.data.data.attempts
          this.attemptsPagination.total = response.data.data.total
        }
      } catch (error) {
        console.error('Error loading attempts:', error)
      } finally {
        this.loadingAttempts = false
      }
    },

    async viewAttemptDetail(attempt) {
      try {
        const response = await postRequest('getStudentExamDetail', {
          attempt_id: attempt.attempt_id,
          user_id: this.$store.state.user_id
        })

        if (response.data.code === 0 && response.data.data) {
          this.attemptDetail = response.data.data
          this.showAttemptDetail = true
        } else {
          this.$message.error(response.data.msg || 'Failed to load detail')
        }
      } catch (error) {
        console.error('Error loading attempt detail:', error)
        this.$message.error('Failed to load attempt detail')
      }
    },

    getBarWidth(count) {
      const max = Math.max(...Object.values(this.analytics.score_distribution), 1)
      return (count / max) * 100
    },

    getBarClass(range) {
      if (range === '81-100') return 'bar-excellent'
      if (range === '61-80') return 'bar-good'
      if (range === '41-60') return 'bar-average'
      if (range === '21-40') return 'bar-poor'
      return 'bar-fail'
    },

    getTrendHeight(count) {
      const max = Math.max(...this.analytics.trends.map(t => t.attempts), 1)
      return Math.max((count / max) * 80, 4)
    },

    getCorrectRateColor(rate) {
      if (rate >= 70) return '#67c23a'
      if (rate >= 40) return '#e6a23c'
      return '#f56c6c'
    },

    getDifficultyType(difficulty) {
      if (difficulty === 'easy') return 'success'
      if (difficulty === 'medium') return 'warning'
      return 'danger'
    },

    formatDuration(seconds) {
      if (!seconds) return '0:00'
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins}:${secs.toString().padStart(2, '0')}`
    },

    formatDate(timestamp) {
      if (!timestamp) return '-'
      return new Date(timestamp * 1000).toLocaleString()
    }
  },
  mounted() {
    // Load all attempts when dialog opens
    this.$watch('showAllAttempts', (val) => {
      if (val) {
        this.attemptsPagination.page = 1
        this.loadAttempts()
      }
    })
  }
}
</script>

<style scoped>
.exam-analytics-dashboard {
  padding: 20px;
}

.empty-hint {
  color: #909399;
  font-size: 14px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
}

.stat-card.highlight-success {
  border-color: #67c23a;
  background: linear-gradient(135deg, #f0f9eb 0%, #fff 100%);
}

.stat-value {
  font-size: 32px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 14px;
  color: #909399;
}

.charts-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
}

.chart-card {
  min-height: 280px;
}

.score-distribution {
  padding: 10px 0;
}

.distribution-bar {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.bar-label {
  width: 60px;
  font-size: 12px;
  color: #606266;
}

.bar-container {
  flex: 1;
  height: 20px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
  margin: 0 12px;
}

.bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s;
}

.bar-excellent { background: #67c23a; }
.bar-good { background: #85ce61; }
.bar-average { background: #e6a23c; }
.bar-poor { background: #f56c6c; }
.bar-fail { background: #909399; }

.bar-count {
  width: 30px;
  font-size: 12px;
  color: #606266;
  text-align: right;
}

.trends-chart {
  height: 180px;
  display: flex;
  flex-direction: column;
}

.trend-bars {
  flex: 1;
  display: flex;
  align-items: flex-end;
  gap: 2px;
  padding-bottom: 10px;
}

.trend-bar-group {
  flex: 1;
  display: flex;
  justify-content: center;
}

.trend-bar {
  width: 100%;
  max-width: 20px;
  background: #409eff;
  border-radius: 2px 2px 0 0;
  cursor: pointer;
}

.trend-bar:hover {
  background: #66b1ff;
}

.trend-legend {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #909399;
  border-top: 1px solid #ebeef5;
  padding-top: 8px;
}

.section-card {
  margin-bottom: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-hint {
  font-size: 12px;
  color: #909399;
  font-weight: normal;
}

.question-preview {
  font-size: 13px;
  color: #606266;
}

.student-name {
  font-weight: 500;
}

.score-pass {
  color: #67c23a;
  font-weight: 600;
}

.score-fail {
  color: #f56c6c;
  font-weight: 600;
}

.warning-text {
  color: #e6a23c;
}

.attempts-filters {
  margin-bottom: 16px;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}

.attempt-detail {
  max-height: 60vh;
  overflow-y: auto;
}

.detail-summary {
  display: flex;
  gap: 24px;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 8px;
  margin-bottom: 20px;
}

.summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.summary-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.summary-value {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.text-success { color: #67c23a; }
.text-danger { color: #f56c6c; }
.text-warning { color: #e6a23c; }

.questions-review h4 {
  margin-bottom: 16px;
  color: #303133;
}

.question-item {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
}

.question-item.correct {
  border-left: 4px solid #67c23a;
}

.question-item.incorrect {
  border-left: 4px solid #f56c6c;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.question-number {
  font-weight: 600;
  color: #303133;
}

.question-text {
  font-size: 14px;
  color: #606266;
  margin-bottom: 12px;
  line-height: 1.6;
}

.question-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-item {
  display: flex;
  align-items: flex-start;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 4px;
}

.option-item.correct-answer {
  background: #f0f9eb;
  border: 1px solid #67c23a;
}

.option-item.wrong-answer {
  background: #fef0f0;
  border: 1px solid #f56c6c;
}

.option-label {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e4e7ed;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  margin-right: 12px;
  flex-shrink: 0;
}

.correct-answer .option-label {
  background: #67c23a;
  color: #fff;
}

.wrong-answer .option-label {
  background: #f56c6c;
  color: #fff;
}

.option-text {
  font-size: 13px;
  color: #606266;
}

.question-explanation {
  margin-top: 12px;
  padding: 10px;
  background: #f0f9eb;
  border-radius: 4px;
  font-size: 13px;
  color: #67c23a;
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .charts-row {
    grid-template-columns: 1fr;
  }
}
</style>
